<template>
  <div id="app" class="min-h-screen bg-casino-dark text-white">
    <div
      v-if="loadingStore.isLoading"
      class="fixed top-0 left-0 right-0 z-[120] h-1 bg-gradient-to-r from-casino-primary/10 via-casino-primary to-casino-primary/10 animate-pulse"
    />
    <!-- Game Route - Fullscreen immersive layout -->
    <template v-if="isGameRoute">
      <router-view />
      <LoginModal
        :is-open="showLoginModal"
        @close="showLoginModal = false"
        @switch-to-register="switchToRegister"
        @success="handleAuthSuccess"
      />
      <RegisterModal
        :is-open="showRegisterModal"
        @close="showRegisterModal = false"
        @switch-to-login="switchToLogin"
        @success="handleAuthSuccess"
      />
      <ToastContainer position="top-right" />
    </template>

    <!-- Normal Layout -->
    <template v-else>
      <div :class="isSportsbookRoute ? 'flex min-h-screen' : 'flex h-screen overflow-hidden'">
        <Sidebar 
          :is-open="isSidebarOpen" 
          :is-mobile="isMobile"
          @close="closeSidebar"
          @toggle="toggleSidebar"
        />
        
        <div class="flex-1 flex flex-col min-w-0 relative">
          <div 
            v-if="isSidebarOpen && isMobile" 
            class="fixed inset-0 bg-black/60 z-[9400] lg:hidden"
            @click="closeSidebar"
          ></div>
          
          <TopBar 
            @open-login="showLoginModal = true"
            @open-register="showRegisterModal = true"
            @toggle-sidebar="toggleSidebar"
            :is-sidebar-open="isSidebarOpen"
          />
          
          <main :class="['flex-1 bg-casino-dark', isSportsbookRoute ? 'min-w-0' : 'overflow-y-auto overflow-x-hidden']">
            <div v-if="!routeReady" class="p-4 sm:p-6 space-y-6 min-h-[65vh]">
              <div class="rounded-2xl border border-white/10 bg-white/[0.03] h-44 sm:h-56 lg:h-64 animate-pulse"></div>
              <div class="flex items-center gap-2 overflow-hidden">
                <div v-for="idx in 5" :key="`app-boot-chip-${idx}`" class="h-8 w-28 rounded-full bg-white/[0.05] animate-pulse flex-shrink-0"></div>
              </div>
              <div class="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                <div
                  v-for="idx in 10"
                  :key="`app-boot-card-${idx}`"
                  class="rounded-xl border border-white/10 bg-white/[0.02] p-3 space-y-2"
                >
                  <div class="h-28 w-full rounded-lg bg-white/[0.05] animate-pulse"></div>
                  <div class="h-4 w-3/4 rounded bg-white/[0.05] animate-pulse"></div>
                  <div class="h-3 w-1/2 rounded bg-white/[0.05] animate-pulse"></div>
                </div>
              </div>
            </div>
            <router-view v-else :key="routeViewKey" />
            <Footer v-if="!isSportsbookRoute" />
          </main>
        </div>
      </div>
      
      <LoginModal
        :is-open="showLoginModal"
        @close="showLoginModal = false"
        @switch-to-register="switchToRegister"
        @success="handleAuthSuccess"
      />
      
      <RegisterModal
        :is-open="showRegisterModal"
        @close="showRegisterModal = false"
        @switch-to-login="switchToLogin"
        @success="handleAuthSuccess"
      />

      <WalletModal
        :is-open="showWalletModal"
        :initial-tab="walletModalTab"
        @close="showWalletModal = false"
        @success="handleWalletSuccess"
      />

      <ToastContainer position="top-right" />
      <!-- OCULTO: LiveChat es un bot mock, no conectado a soporte real.
      <LiveChat v-if="showLiveChat" />
      -->
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, provide, watch, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useGamesStore } from '@/stores/games'
import Sidebar from '@/components/Sidebar.vue'
import TopBar from '@/components/TopBar.vue'
import Footer from '@/components/Footer.vue'
import LoginModal from '@/components/LoginModal.vue'
import RegisterModal from '@/components/RegisterModal.vue'
import WalletModal from '@/components/WalletModal.vue'
import ToastContainer from '@/components/ToastContainer.vue'
// OCULTO: LiveChat es un bot mock, no se usa
// import LiveChat from '@/components/LiveChat.vue'
import { useLoadingStore } from '@/stores/loading'
import { useNotificationStore } from '@/stores/notifications'
import { t } from '@/i18n'

const SESSION_HEARTBEAT_INTERVAL = 5 * 60 * 1000
let sessionHeartbeatTimer = null

const authStore = useAuthStore()
const gamesStore = useGamesStore()
const loadingStore = useLoadingStore()
const route = useRoute()
const router = useRouter()

const showLoginModal = ref(false)
const showRegisterModal = ref(false)
const showWalletModal = ref(false)
const walletModalTab = ref('deposit')
const routeReady = ref(false)

const isSidebarOpen = ref(window.innerWidth >= 1024)
const isMobile = ref(window.innerWidth < 1024)


// FIX BUG 2: Shared ref for Betby navigation.
// Sportsbook.vue sets this when the BTRenderer instance is available.
// Sidebar.vue reads this to decide whether to call updateOptions (inside sportsbook)
// or use normal router navigation (outside sportsbook).
const betbyNavigateFn = ref(null)
const isGameRoute = computed(() => route.path.startsWith('/game/'))
const isSportsbookRoute = computed(() => route.path === '/sportsbook')
// OCULTO: LiveChat mock desactivado
// const showLiveChat = computed(() => !isSportsbookRoute.value)
const routeViewKey = computed(() => (
  isSportsbookRoute.value
    ? 'sportsbook'
    : route.fullPath
))

const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value
}

const closeSidebar = () => {
  if (isMobile.value) {
    isSidebarOpen.value = false
  }
}

const handleResize = () => {
  isMobile.value = window.innerWidth < 1024
  if (window.innerWidth >= 1024) {
    isSidebarOpen.value = true
  } else {
    isSidebarOpen.value = false
  }
}

const openLogin = () => {
  showRegisterModal.value = false
  showLoginModal.value = true
}

const openRegister = () => {
  showLoginModal.value = false
  showRegisterModal.value = true
}

const switchToRegister = () => {
  showLoginModal.value = false
  showRegisterModal.value = true
}

const switchToLogin = () => {
  showRegisterModal.value = false
  showLoginModal.value = true
}

const openWallet = (tab = 'deposit') => {
  if (!authStore.isAuthenticated) {
    openLogin()
    return
  }

  walletModalTab.value = tab
  showWalletModal.value = true
}

const refreshBalanceOnNavigation = async ({ force = false } = {}) => {
  if (!authStore.isAuthenticated) return
  if (!force && gamesStore.balanceLoading) return
  await gamesStore.fetchBalance()
}

const handleAuthSuccess = () => {
  void refreshBalanceOnNavigation({ force: true })
  void gamesStore.fetchGames()
  if (isSportsbookRoute.value) {
    window.location.reload()
  }
}

const handleWalletSuccess = () => {
  void refreshBalanceOnNavigation({ force: true })
}

const consumeAuthQuery = async (value) => {
  const authMode = String(value || '').trim().toLowerCase()
  if (authMode === 'login') {
    openLogin()
  } else if (authMode === 'register') {
    openRegister()
  } else {
    return
  }

  const nextQuery = { ...route.query }
  delete nextQuery.auth
  await router.replace({
    path: route.path,
    query: nextQuery,
    hash: route.hash,
  })
}

provide('openLogin', openLogin)
provide('openRegister', openRegister)
provide('openWallet', openWallet)
provide('isSidebarOpen', isSidebarOpen)
provide('toggleSidebar', toggleSidebar)
provide('closeSidebar', closeSidebar)
provide('isMobile', isMobile)
provide('betbyNavigateFn', betbyNavigateFn)

const startSessionHeartbeat = () => {
  stopSessionHeartbeat()
  sessionHeartbeatTimer = window.setInterval(async () => {
    if (!authStore.isAuthenticated) {
      stopSessionHeartbeat()
      return
    }
    const result = await authStore.refreshSession()
    if (!result.success) {
      stopSessionHeartbeat()
      authStore.clearSession()
      useNotificationStore().warning(t('session.expiredMessage'), t('session.expiredTitle'))
      window.location.href = '/?auth=login'
    }
  }, SESSION_HEARTBEAT_INTERVAL)
}

const stopSessionHeartbeat = () => {
  if (sessionHeartbeatTimer) {
    window.clearInterval(sessionHeartbeatTimer)
    sessionHeartbeatTimer = null
  }
}

onMounted(async () => {
  router.isReady().then(() => {
    routeReady.value = true
  })

  await authStore.restoreSession()
  if (authStore.isAuthenticated) {
    await refreshBalanceOnNavigation({ force: true })
    startSessionHeartbeat()
  }
  window.addEventListener('resize', handleResize)
  handleResize()
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  stopSessionHeartbeat()
})

watch(
  () => route.query.auth,
  (value) => {
    if (!value) return
    consumeAuthQuery(value)
  },
  { immediate: true },
)

watch(
  () => route.fullPath,
  () => {
    void refreshBalanceOnNavigation()
  },
)

watch(
  () => authStore.isAuthenticated,
  (isAuthenticated) => {
    if (!isAuthenticated) {
      stopSessionHeartbeat()
      return
    }
    void refreshBalanceOnNavigation({ force: true })
    startSessionHeartbeat()
  },
)
</script>

<style>
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: #1a1a2e;
}

::-webkit-scrollbar-thumb {
  background: #374151;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #4b5563;
}

html {
  scroll-behavior: smooth;
}
</style>
